import React, {useMemo, useState} from "react";
import {Badge, Button, Col, Divider, Form, Input, Row, Space, Tabs, Upload} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "./catalog.search.scss"
import {UIInputWidgetManufacturer} from "../ui/input/widget/manufacturer/ui.input.widget.manufacturer";
import {UIInputWidgetSearch} from "../ui/input/widget/search/ui.input.widget.search";
import {UIInputWidgetModel} from "../ui/input/widget/model/ui.input.widget.model";
import {UIInputWidgetEngine} from "../ui/input/widget/engine/ui.input.widget.engine";
import {UIInputWidgetGroup} from "../ui/input/widget/group/ui.input.widget.group";
import {UIInputWidgetSubGroup} from "../ui/input/widget/subgroup/ui.input.widget.subgroup";
import {SearchParams, SearchType} from "../model/search";
import {SuitableVehicle} from "../../../models/articles/catalog/search";
import {useTranslation} from "react-i18next";
import {UIInputWidgetVIN} from "../ui/input/widget/vin/ui.input.widget.vin";

interface CatalogSearchProps {
    onChange: (params: SearchParams) => void;
    onOEMListSearch: (file: File) => Promise<any>;
    defaultValues: any;
    vehicle?: SuitableVehicle;
}

export const CatalogSearch: React.FC<CatalogSearchProps> = ({onChange, onOEMListSearch, defaultValues, vehicle}) => {

    const {t} = useTranslation('translation', {useSuspense: false});

    const [form] = Form.useForm();

    const [disabled, setDisabled] = useState<boolean>(false);

    const defaultValuesNormalized = useMemo(() => {
        const { type, ...defaultFormValues } = defaultValues;
        return {
            defaultType: type || SearchType.PART,
            defaultFormValues: defaultFormValues
        }
    }, [defaultValues])

    const onChangeTab = (searchType: string) => {
        onChange({type: searchType as SearchType});
        setTimeout(() => {
            form.resetFields();
        }, 0);
    }

    return (
        <div className="catalog_search_block_container">
            <div id="catalog_search_block" className="catalog_search_block">
                <Form
                    form={form}
                    style={{width: "100%"}}
                    initialValues={defaultValuesNormalized.defaultFormValues}
                    onFinish={() =>
                        form.validateFields().then((values) => {
                            onChange({type: defaultValuesNormalized.defaultType, ...values});
                        })
                    }
                >
                    <Tabs
                        onChange={onChangeTab}
                        defaultActiveKey={defaultValuesNormalized.defaultType}
                        destroyInactiveTabPane
                    >
                        <Tabs.TabPane tab={t('CATALOG.SEARCH.TYPE.PART')} key={SearchType.PART}>
                            <Form.Item name="searchString" noStyle>
                                <UIInputWidgetSearch
                                    onChange={() => form.submit()}
                                />
                            </Form.Item>
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={t('CATALOG.SEARCH.TYPE.CAR')} key={SearchType.CAR}>
                            <Row gutter={[20, 20]}>
                                <Col className="selects-col-icon" xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Row align="middle" gutter={8} wrap={false}>
                                        <Col flex="none">
                                            <FontAwesomeIcon color={"#D7D7D7"} icon={["fas", "car"]} style={{marginRight: 7.5}} size={"lg"}/>
                                        </Col>
                                        <Col flex="auto">
                                            <Form.Item name="manufacturer" noStyle>
                                                <UIInputWidgetManufacturer
                                                    onClear={() => form.setFieldsValue({engine: undefined, model: undefined})}
                                                    onChange={() => form.submit()}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xxl={4} xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(v1, v2) => v1.manufacturer !== v2.manufacturer}
                                    >
                                        {() => (
                                            <Form.Item noStyle name="model">
                                                <UIInputWidgetModel
                                                    manufacturerId={form.getFieldValue('manufacturer')}
                                                    onClear={() => form.setFieldsValue({engine: undefined})}
                                                    onChange={() => form.submit()}
                                                />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col xxl={4} xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(v1, v2) => v1.model !== v2.model}
                                    >
                                        {() => (
                                            <Form.Item noStyle name="engine">
                                                <UIInputWidgetEngine
                                                    modelId={form.getFieldValue('model')}
                                                    onChange={() => form.submit()}
                                                />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Row align="middle" gutter={8} wrap={false}>
                                        <Col flex="none">
                                            <Divider
                                                orientation={"center"}
                                                type={"vertical"}
                                                className="selects_block_divider selects_block_divider_vertical"
                                            />
                                        </Col>
                                        <Col flex="none">
                                            <FontAwesomeIcon color={"#D7D7D7"} icon={["fas", "cubes"]} style={{marginRight: 7.5}} size={"lg"}/>
                                        </Col>
                                        <Col flex="auto">
                                            <Form.Item noStyle name="group">
                                                <UIInputWidgetGroup
                                                    onChange={() => form.submit()}
                                                    onClear={() => form.setFieldsValue({subgroup: undefined})}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xxl={4} xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(v1, v2) => v1.group !== v2.group}
                                    >
                                        {() => (
                                            <Form.Item noStyle name="subgroup">
                                                <UIInputWidgetSubGroup
                                                    groupId={form.getFieldValue('group')}
                                                    onChange={() => form.submit()}
                                                />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            tab={
                                <Badge count="New" size={"small"} color={"#f93822"}>
                                    <span>{t('CATALOG.SEARCH.TYPE.VIN')}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                                </Badge>}
                            key={SearchType.VIN}
                        >
                            <Row gutter={[20, 20]}>
                                <Col xxl={14} xl={14} lg={14} md={14} sm={14} xs={14}>
                                    <Row align="middle" gutter={8} wrap={false} style={{alignItems: "start"}}>
                                        <Col flex="none" style={{paddingTop: "6px"}}>
                                            <FontAwesomeIcon color={"#D7D7D7"} icon={["fas", "car"]} style={{marginRight: 7.5}} size={"lg"}/>
                                        </Col>
                                        <Col flex="auto">
                                            <Space direction={"vertical"}>
                                                <Form.Item
                                                    name="vehicleId"
                                                    noStyle
                                                >
                                                    <UIInputWidgetVIN
                                                        onChange={() => form.submit()}
                                                    />
                                                </Form.Item>
                                                {vehicle ? (
                                                    <div className={"corresponds-vehicle"}>
                                                        <div className={"vin-title"}>{vehicle.brand} {vehicle.model}</div>
                                                        <div
                                                            className={"vin-attributes"}>{vehicle.attributes.map(a => a.label + ': ' + a.value).join(', ')}</div>
                                                    </div>
                                                ) : null}
                                            </Space>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Row align="middle" gutter={8} wrap={false}>
                                        <Col flex="none">
                                            <Divider
                                                orientation={"center"}
                                                type={"vertical"}
                                                className="selects_block_divider selects_block_divider_vertical"
                                            />
                                        </Col>
                                        <Col flex="none">
                                            <FontAwesomeIcon color={"#D7D7D7"} icon={["fas", "cubes"]} style={{marginRight: 7.5}} size={"lg"}/>
                                        </Col>
                                        <Col flex="auto">
                                            <Form.Item noStyle name="group">
                                                <UIInputWidgetGroup
                                                    onChange={() => form.submit()}
                                                    onClear={() => form.setFieldsValue({subgroup: undefined})}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xxl={4} xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(v1, v2) => v1.group !== v2.group}
                                    >
                                        {() => (
                                            <Form.Item noStyle name="subgroup">
                                                <UIInputWidgetSubGroup
                                                    groupId={form.getFieldValue('group')}
                                                    onChange={() => form.submit()}
                                                />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            tab={
                                <Badge count="New" size={"small"} color={"#f93822"}>
                                    <span>{t('CATALOG.SEARCH.TYPE.OEM-LIST')}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                                </Badge>}
                            key={SearchType.OEM_LIST}
                        >
                            <Upload.Dragger
                                style={{width: "100%"}}
                                name={"file"}
                                multiple={false}
                                customRequest={async r => {
                                    if (r.onSuccess) {
                                        r.onSuccess(await onOEMListSearch(r.file as File))
                                    } else {
                                        await onOEMListSearch(r.file as File)
                                    }
                                }}
                                showUploadList={false}
                                accept={".csv,.txt"}
                                disabled={disabled}
                                onChange={(info) => {
                                    const { status } = info.file;
                                    switch (status) {
                                        case 'uploading':
                                            setDisabled(true)
                                            break;
                                        case 'error':
                                        case 'success':
                                        case 'done':
                                            setDisabled(false)
                                            break;
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={["fas", "file-upload"]} size={"2x"}/>
                                <p>{t("UPLOAD.TEXT")}</p>
                                <p>
                                    {t("CATALOG.SEARCH.OEM.UPLOAD.INFO_TEXT")}
                                </p>
                            </Upload.Dragger>
                        </Tabs.TabPane>
                    </Tabs>
                </Form>
            </div>
        </div>
    )
}